<template lang="html">
    <div class="payment-single-row">

        <!-- Header -->
        <div
            class="lightest larger bold d-flex p-2 pointer"
            :class="paymentColor(payment)"
            @click="expandPaymentClick()"
        >
            <div class="flex-grow-1 d-flex justify-content-between align-items-center">
                <div class="d-flex align-items-center">
                    {{paymentTitle(payment)}}
                    <span v-if="isRefundProcessed" class="ml-2">(Refunded)</span>
                    <span v-if="isRefundCanceled" class="ml-2">(Canceled)</span>
                </div>
                <div class="payment-amount">
                    {{paymentValue(payment)}}
                </div>
            </div>

            <div>
                <span>{{payment.code}}</span>
                <span class="ml-2">
                    <i v-if="isExpanded" class="fas fa-chevron-up"></i>
                    <i v-else class="fas fa-chevron-down"></i>
                </span>
            </div>
        </div>

        <!-- Content -->
        <div v-if="isExpanded" class="bg-faded border-light p-2">

            <refund-area
                v-if="thisPaymentRefund"
                :refund="thisPaymentRefund"
                :payment="payment"
                :reservation="reservation"
                :isLegacyRefund="true"
            />

            <pay-in-area
                v-else
                :payment="mergedPayment"
                :reservation="reservation"
                :isFullyRefunded="isFullyRefunded"
                @change="changed()"
            />

        </div>

    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import PAYMENT_DIRECTION from '@/enums/paymentDirection'

// areas
import payInArea from './expandedArea/payIn'
import refundArea from './expandedArea/refund'

const PaymentStatusEnum = {
    Paid: 0,
    PaymentMissing: 1,
    Refunded: 2,
    PartiallyRefunded: 3,
    Legacy: 4
}

const paymentStatusColorMap = {
    [PaymentStatusEnum.Paid]: 'bg-color-3',
    [PaymentStatusEnum.PaymentMissing]: 'bg-color-5',
    [PaymentStatusEnum.Refunded]: 'bg-color-6',
    [PaymentStatusEnum.PartiallyRefunded]: 'bg-payment-partially-refunded',
    [PaymentStatusEnum.Legacy]: 'bg-color-2'
}

export default {
    created: function () {
        // Set refund as expanded if the link contains the same id, example: localhost:8081/reservations/view/27656?refundId=3#refunds
        if (this.$route.query.refundId && this.$route.query.refundId == this.payment.id) {
            this.isExpanded = true
        }
    },
    props: {
        reservation: {
            type: Object
        },
        payments: {
            type: Array,
            required: true,
            default: function () {
                return []
            }
        },
        refunds: {
            type: Array,
            default: function () {
                return []
            }
        },
        isUnsavedRefund: {
            type: Boolean,
            default: false
        }
    },
    data: function () {
        return {
            isExpanded: false,
        }
    },
    computed: {
        ...mapGetters([
            'getRefundReasons',
            'getPercentageOrCurrency',
            'getPaymentTypeListAll',
            'getRefundStatus'
        ]),
        payment () {
            return this.payments[0] || {}
        },
        groupPaymentStatus () {
            if (this.payments.length === 1) return this.payment.paymentStatus
            if (this.payments.every(p => p.paymentStatus === PaymentStatusEnum.Refunded)) {
                return PaymentStatusEnum.Refunded
            }
            if (this.payments.some(p =>
                p.paymentStatus === PaymentStatusEnum.Refunded ||
                p.paymentStatus === PaymentStatusEnum.PartiallyRefunded
            )) {
                return PaymentStatusEnum.PartiallyRefunded
            }
            return this.payment.paymentStatus
        },
        isFullyRefunded () {
            return this.groupPaymentStatus === PaymentStatusEnum.Refunded
        },
        isUnsavedPayment: function () {
            return this.payment.id == null
        },
        isPayIn: function () {
            return this.payment.paymentDirection === PAYMENT_DIRECTION.PAY_IN
        },
        isRefund: function () {
            return !!this.payment.refundId
        },
        isRefundProcessed: function () {
            return !!(this.thisPaymentRefund && this.thisPaymentRefund.isProcessed)
        },
        isRefundCanceled: function () {
            return !!(this.thisPaymentRefund && this.thisPaymentRefund.status === -100)
        },
        thisPaymentRefund: function () {
            if (this.isUnsavedRefund) return this.payment.refundObj
            if (!this.isRefund) return false
            return this.refunds.find(refund => refund.id == this.payment.refundId)
        },
        mergedPayment () {
            if (this.payments.length === 1) return this.payments[0]
            const primary = this.payments[0]
            const totalPaidValue = this.payments.reduce((sum, p) => sum + (p.paidValue || 0), 0)
            const allAllocations = this.payments.flatMap(p => p.allocations || [])
            const seenInvoiceIds = new Set()
            const allInvoices = this.payments.flatMap(p => p.invoices || []).filter(inv => {
                if (seenInvoiceIds.has(inv.id)) return false
                seenInvoiceIds.add(inv.id)
                return true
            })
            const allComponents = this.payments.flatMap(p => p.components || [])
            const allTickets = this.payments.flatMap(p => p.tickets || [])
            const seenRefundIds = new Set()
            const allRefunds = this.payments.flatMap(p => p.refunds || []).filter(refund => {
                if (seenRefundIds.has(refund.id)) return false
                seenRefundIds.add(refund.id)
                return true
            })
            const canBeRefunded = this.payments.some(p => p.canBeRefunded)
            return {
                ...primary,
                isGrouped: true,
                paidValue: totalPaidValue,
                allocations: allAllocations,
                invoices: allInvoices,
                components: allComponents,
                tickets: allTickets,
                refunds: allRefunds,
                canBeRefunded,
            }
        }
    },
    methods: {
        ...mapActions([
            'openModal',
            'executeRefund',
            'selectRefund',
        ]),
        paymentTitle: function (payment) {
            const titleParts = []

            if (this.paymentDate(payment)) {
                titleParts.push(this.paymentDate(payment))
            }

           if (this.isPayIn && !this.isRefund && !this.isUnsavedRefund) {
                if (this.payments.length > 1) {
                    const invoicesCount = (this.mergedPayment.invoices || []).length
                    const invoiceLabel = invoicesCount === 1 ? 'invoice' : 'invoices'
                    return `${titleParts.join(' - ')} • ${this.payments.length} payments • ${invoicesCount} ${invoiceLabel}`
                }

                const paymentsCount = payment.isFullAmount ? 1 : (payment.allocations ? 1 : 0)
                const invoicesCount = payment.invoices ? payment.invoices.length : 0

                const paymentLabel = paymentsCount === 1 ? 'payment' : 'payments'
                const invoiceLabel = invoicesCount === 1 ? 'invoice' : 'invoices'

                return `${titleParts.join(' - ')} • ${paymentsCount} ${paymentLabel} • ${invoicesCount} ${invoiceLabel}`
            }

            if (this.isUnsavedPayment) {
                titleParts.push('Unsaved ')
            }
            if (this.paymentStatus(payment)) {
                titleParts.push(this.paymentStatus(payment))
            }
            if (this.paymentMethod(payment)) {
                titleParts.push(this.paymentMethod(payment))
            }
            if (this.paymentChannel(payment)) {
                titleParts.push(this.paymentChannel(payment))
            }

            return titleParts.join(' - ')
        },
        expandPaymentClick: function () {
            this.isExpanded = !this.isExpanded
        },
        paymentDate(payment) {
            const date = payment.date || payment.createdOn
            return `Transaction ${payment.friendlyTransactionCode} started on ${moment(date).format('D MMMM YYYY [at] HH:mm')}`
        },
        paymentStatus: function (payment) {
            if (this.isUnsavedRefund) {
                if (this.payment.refundObj && this.payment.refundObj.id) {
                    const foundStatus = this.getRefundStatus.find(s => s.id == this.payment.refundObj.status)
                    return foundStatus ? foundStatus.label : ''
                }
                return 'Unsaved Refund'
            }
            if (payment.paymentDirection === PAYMENT_DIRECTION.PAY_IN) return 'Pay In'
            if (!this.isRefund) return 'Pay Out'
            if (this.thisPaymentRefund) {
                const foundStatus = this.getRefundStatus.find(status => status.id == this.thisPaymentRefund.status)
                return foundStatus ? foundStatus.label : ''
            }
            return 'Loading'
        },
        paymentMethod: function (payment) {
            const typeId = this.isUnsavedRefund ? payment.refundObj.paymentType : payment.type
            const foundPaymentMethod = this.getPaymentTypeListAll.find(paymentType => paymentType.id == typeId)
            return foundPaymentMethod ? foundPaymentMethod.description : null
        },
        paymentChannel: function (payment) {
            if (this.isUnsavedRefund) return payment.refundObj.refundChannels[0].brandName
            if (payment.treasurerUserAccount) return
            if (this.reservation.channelTypeDescription) return this.reservation.channelTypeDescription
            return null
        },
        paymentColor(payment) {
            if (this.isUnsavedRefund || this.isUnsavedPayment) {
                return 'bg-color-1'
            }

            const effectiveStatus = this.payments.length > 1 ? this.groupPaymentStatus : payment.paymentStatus
            if (effectiveStatus !== undefined && effectiveStatus !== null) {
                const mappedColor = paymentStatusColorMap[effectiveStatus]
                if (mappedColor) return mappedColor
            }

            if (payment.paymentDirection === PAYMENT_DIRECTION.PAY_IN) {
                return 'bg-color-3'
            }

            if (this.isRefund && this.thisPaymentRefund) {
                const foundStatus = this.getRefundStatus.find(
                    status => status.id == this.thisPaymentRefund.status
                )
                if (foundStatus && foundStatus.color) {
                    return `bg-${foundStatus.color}`
                }
            }

            if (!this.isRefund) {
                return 'bg-color-6'
            }

            return 'bg-color-1'
        },
        paymentValue: function (payment) {
            if (this.isUnsavedRefund) return this.formatPrice(payment.refundObj.amountResumeValue) + '€'
            if (this.payments.length > 1) {
                const total = this.payments.reduce((sum, p) => sum + (p.paidValue || 0), 0)
                return this.formatPrice(total) + payment.currency
            }
            return this.formatPrice(payment.paidValue) + payment.currency
        },
        changed: function () {
            this.$set(this.reservation.updateFlags, 'billing', true)
        }
    },
    components: {
        payInArea,
        refundArea,
    }
}
</script>

<style lang="scss" scoped>
@keyframes blink {
    0% {
        opacity: 0;
    }
    33% {
        opacity: 1;
    }
    66% {
        opacity: 0;
    }
    100% {
        opacity: 1;
    }
}

.btnMarkAsRefunded {
    animation: 1s ease-out 0s 1 blink;
}

.payment-single-row {
    border-radius: 0.25rem;
    overflow: hidden;
}

.pay-in-count-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    width: 1.25rem;
    height: 1.25rem;
    font-size: 0.7rem;
    font-weight: 700;
}

.payment-amount {
    white-space: nowrap;
    margin-left: 1rem;
    font-weight: 700;
}

.bg-payment-partially-refunded {
    background-color: #e8991f;
}
</style>


// WEBPACK FOOTER //
// paymentSingleRow.vue?3364733c